<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    DocumentTemplatesService,
    WordTemplateStatus,
    type CustomerDocumentTemplate,
    type CustomerUser as User,
    type LawFields,
    LawFieldsService
  } from '@/types/generated'
  import { onMounted, ref, computed, watch } from 'vue'
  import * as yup from 'yup'
  import { useRouter } from 'vue-router'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import { SlashIcon } from '@heroicons/vue/24/outline'
  import { useUserStore } from '@/shared/stores/userStore'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import FileField from '@/components/admin/form/FileField.vue'
  import {
    WordDocumentTemplatesService,
    type CustomerWordDocumentTemplate
  } from '@/types/generated'
  import { CopyIcon, DownloadIcon, TrashIcon } from 'lucide-vue-next'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'

  const { t } = useTranslation({
    en: {
      duplicate: 'Duplicate',
      delete: 'Delete',
      download: 'Download',
      submitForReview: 'Submit for Review',
      submitting: 'Submitting...',
      cancel: 'Cancel',
      approved: 'Approved',
      draft: 'Draft',
      underReview: 'Under Review',
      jupusTemplateNotice:
        'JUPUS templates cannot be edited directly. Click "Duplicate" to create an editable copy.',
      underReviewNotice:
        'This template is currently under review. Changes will only be possible after the review is completed.',
      approvedNotice:
        'This template has been approved and is ready to use. If you update the template file, it will need to be submitted for review again.',
      submitInstructions:
        'When you are finished editing, click "Submit for Review" to submit the template for review.',
      submitNote:
        'Note: After submitting for review, you will not be able to edit this template until the review is complete.',
      confirmTitle: 'Submit for Review',
      confirmMessage:
        'After submitting for review, you will not be able to edit this template until the review is complete. Do you want to proceed?',
      originalDocument: 'Original document',
      templateWithPlaceholders: 'Template with placeholders'
    },
    de: {
      duplicate: 'Duplizieren',
      delete: 'Löschen',
      download: 'Herunterladen',
      submitForReview: 'Zur Überprüfung einreichen',
      submitting: 'Wird eingereicht...',
      cancel: 'Abbrechen',
      approved: 'Genehmigt',
      draft: 'Entwurf',
      underReview: 'In Überprüfung',
      jupusTemplateNotice:
        'JUPUS Vorlagen können nicht direkt bearbeitet werden. Klicken Sie auf "Duplizieren" um eine bearbeitbare Kopie zu erstellen.',
      underReviewNotice:
        'Diese Vorlage wird derzeit überprüft. Änderungen sind erst möglich, nachdem die Überprüfung abgeschlossen ist.',
      approvedNotice:
        'Diese Vorlage wurde genehmigt und ist einsatzbereit. Wenn Sie die Vorlagendatei aktualisieren, muss sie erneut zur Überprüfung eingereicht werden.',
      submitInstructions:
        'Wenn Sie mit der Bearbeitung fertig sind, klicken Sie auf "Zur Überprüfung einreichen", um die Vorlage zur Überprüfung einzureichen.',
      submitNote:
        'Hinweis: Nach dem Einreichen zur Überprüfung können Sie diese Vorlage nicht mehr bearbeiten, bis die Überprüfung abgeschlossen ist.',
      confirmTitle: 'Vorlage zur Überprüfung einreichen',
      confirmMessage:
        'Nach dem Einreichen zur Überprüfung können Sie diese Vorlage nicht mehr bearbeiten, bis die Überprüfung abgeschlossen ist. Möchten Sie fortfahren?',
      originalDocument: 'Original Dokument',
      templateWithPlaceholders: 'Vorlage mit Platzhaltern'
    }
  })

  const router = useRouter()
  const { fetchUser } = useUserStore()

  const showConfirmDialog = ref(false)
  const isSubmittingForReview = ref(false)

  const template = ref<CustomerWordDocumentTemplate | undefined>()
  const templateUnderReview = computed(
    () => template.value?.status === WordTemplateStatus.IN_REVIEW
  )
  const templateApproved = computed(() => template.value?.status === WordTemplateStatus.APPROVED)
  const uploadedFile = ref<File | null>(null)
  const docxTemplateCleanUrl = computed(() => {
    if (!template.value?.docx_template) return ''
    return template.value.docx_template.split('/word_document_templates/')[1]?.split('?')[0]
  })
  const originalTemplateCleanUrl = computed(() => {
    if (!template.value?.original_docx_template) return ''
    const parts = template.value.original_docx_template.split('/word_document_templates/')
    return parts[1]?.split('?')[0] || template.value.original_docx_template
  })

  const showTemplateWithPlaceholders = computed(() => {
    return template.value?.status === WordTemplateStatus.APPROVED && template.value?.docx_template
  })

  const user = computed<User>(() => useUserStore().user as User)
  const lawFields = ref<LawFields | null>(null)
  const lawFieldOptions = computed(() => useUserStore().lawFieldOptions)
  const { lawTopicTranslations } = useLawTopicTranslation()

  const lawTopicOptions = computed<Option[]>(() => {
    if (!template.value || !template.value.law_field) {
      return []
    }

    const translations = lawTopicTranslations.value

    return (
      lawFields.value?.data[template.value.law_field]?.map((topic) => ({
        value: topic,
        title: translations[topic] || topic
      })) || []
    ).sort((a, b) => a.title.localeCompare(b.title))
  })

  const fetchTemplate = async () => {
    if (!template.value) {
      template.value = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: router.currentRoute.value.params.uuid.toString()
      })) as CustomerWordDocumentTemplate
    }
  }

  const fetchLawFields = async () => {
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  }

  const handleFormSave = async (values: any) => {
    if (!template.value || template.value?.is_jupus_template || !template.value.uuid) return
    if (template.value.status === WordTemplateStatus.IN_REVIEW) return
    try {
      const formData: any = {
        ...values,
        name: template.value.name,
        description: template.value.description,
        system_prompt: template.value.system_prompt,
        law_field: template.value.law_field,
        law_topic: template.value.law_topic
      }

      // Handle file upload if provided
      if (values.docx_template) {
        formData.docx_template = values.docx_template

        // If template is approved and file is changed, reset status to draft
        if (template.value.status === WordTemplateStatus.APPROVED) {
          formData.status = WordTemplateStatus.DRAFT // Reset to draft/uploaded status
        }
      }

      await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
        uuid: template.value.uuid,
        formData
      })

      // Store current values before fetching
      const currentName = template.value.name
      const currentDescription = template.value.description
      const currentSystemPrompt = template.value.system_prompt

      // Fetch updated template
      const updatedTemplate = await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid
      })

      // Update template but preserve current user input values
      template.value = {
        ...updatedTemplate,
        name: currentName,
        description: currentDescription,
        system_prompt: currentSystemPrompt
      } as CustomerWordDocumentTemplate

      // Reset the file input if a file was uploaded
      if (uploadedFile.value) {
        uploadedFile.value = null
      }
    } catch (error) {
      console.error('Error saving template:', error)
    }
  }

  const requestReview = async () => {
    if (!template.value || template.value?.is_jupus_template || !template.value.uuid) return

    // Show confirmation dialog
    showConfirmDialog.value = true
  }

  const confirmReview = async () => {
    if (!template.value || template.value?.is_jupus_template || !template.value.uuid) return

    try {
      isSubmittingForReview.value = true

      // Update the template status to in_review
      await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
        uuid: template.value.uuid,
        formData: { status: WordTemplateStatus.IN_REVIEW }
      })

      // Directly fetch the updated template to refresh the UI
      const updatedTemplate = await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid
      })

      // Update the template data
      template.value = updatedTemplate as CustomerWordDocumentTemplate

      showConfirmDialog.value = false
    } finally {
      isSubmittingForReview.value = false
    }
  }

  const cancelReview = () => {
    showConfirmDialog.value = false
  }

  const deleteDocumentTemplate = async () => {
    if (!template.value || template.value?.is_jupus_template || !template.value.uuid) return

    try {
      if (template.value.status === WordTemplateStatus.APPROVED) {
        await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
          uuid: template.value.uuid,
          formData: { status: WordTemplateStatus.DRAFT }
        })
      }

      await WordDocumentTemplatesService.wordDocumentTemplatesDeleteDocumentCreate({
        uuid: template.value.uuid
      })

      const updatedTemplate = await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid
      })

      template.value = updatedTemplate as CustomerWordDocumentTemplate

      if (uploadedFile.value) {
        uploadedFile.value = null
      }
    } catch (error) {
      console.error('Error deleting template:', error)
    }
  }

  const copyTemplate = async () => {
    if (!template.value?.uuid) return
    const newTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesCopyCreate({
      uuid: template.value.uuid
    })) as CustomerWordDocumentTemplate
    await router.push(
      ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL.replace(
        ':uuid',
        newTemplate.uuid ?? ''
      )
    )
  }

  onMounted(async () => {
    const fetching = [fetchTemplate(), fetchLawFields()]
    if (!user.value) {
      fetching.push(
        (async () => {
          await fetchUser()
        })()
      )
    }
    await Promise.all(fetching)
  })
</script>
<template>
  <SettingsPage v-if="template && user && lawFields">
    <template #breadcrumbs-end>
      <li class="inline-flex items-center truncate text-sm font-semibold text-gray-800">
        <SlashIcon class="size-5 shrink-0 text-gray-500" />
        {{ template.name }}
        <!-- Status badge -->
        <span
          v-if="templateApproved"
          class="ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium
            text-green-800"
        >
          {{ t('approved') }}
        </span>
        <span
          v-else-if="templateUnderReview"
          class="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium
            text-yellow-800"
        >
          {{ t('underReview') }}
        </span>
        <span
          v-else
          class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium
            text-gray-800"
        >
          {{ t('draft') }}
        </span>
      </li>
    </template>
    <template #actions>
      <div class="flex items-center space-x-2">
        <button
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
          @click="copyTemplate"
        >
          <CopyIcon class="h-5 w-5" />
          {{ t('duplicate') }}
        </button>
      </div>
    </template>
    <div v-if="template.is_jupus_template">
      <div class="mb-4 border-l-4 border-yellow-400 bg-yellow-50 p-4">
        <p class="text-sm font-semibold text-yellow-700">
          {{ t('jupusTemplateNotice') }}
        </p>
      </div>
    </div>
    <div v-else-if="templateUnderReview">
      <div class="mb-4 border-l-4 border-purple-400 bg-purple-50 p-4">
        <p class="text-sm font-semibold text-purple-700">
          {{ t('underReviewNotice') }}
        </p>
      </div>
    </div>
    <div v-else-if="templateApproved">
      <div class="mb-4 border-l-4 border-green-400 bg-green-50 p-4">
        <p class="text-sm font-semibold text-green-700">
          {{ t('approvedNotice') }}
        </p>
      </div>
    </div>
    <div v-else-if="template.docx_template">
      <div class="mb-4 border-l-4 border-blue-400 bg-blue-50 p-4">
        <p class="text-sm font-semibold text-blue-700">
          {{ t('submitInstructions') }}
          <strong>{{ t('submitNote').split(': ')[0] }}:</strong>
          {{ t('submitNote').split(': ')[1] }}
        </p>
      </div>
    </div>
    <AutoSavingForm
      :debounce-delay="1500"
      @save="handleFormSave"
    >
      <div class="mb-4">
        <InputField
          v-model="template.name"
          name="name"
          label="Name"
          :rules="yup.string().required()"
          :disabled="template.is_jupus_template || templateUnderReview"
        />
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <SelectField
            v-model="template.law_field"
            :options="lawFieldOptions"
            name="law_field"
            title="Rechtsgebiet"
            label="Rechtsgebiet"
            :allow-unselect="true"
            unselected-option-text="Alle Rechtsgebiete"
            :disabled="template.is_jupus_template || templateUnderReview"
          />
          <SelectField
            v-model="template.law_topic"
            :options="lawTopicOptions"
            name="law_topic"
            title="Rechtsthema"
            label="Rechtsthema"
            :allow-unselect="true"
            unselected-option-text="Alle Rechtsthemen"
            :disabled="template.is_jupus_template || templateUnderReview"
          />
        </div>
        <TextAreaField
          v-model="template.description"
          name="description"
          label="Beschreibung"
          :disabled="template.is_jupus_template || templateUnderReview"
          :rules="yup.string().required()"
        />
      </div>
    </AutoSavingForm>

    <!-- Display original uploaded document if it exists -->
    <div
      v-if="template.original_docx_template"
      class="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
    >
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-sm font-medium text-gray-700">{{ t('originalDocument') }}</h3>
          <p class="truncate text-sm text-gray-900">{{ originalTemplateCleanUrl }}</p>
        </div>
        <div class="flex gap-2">
          <a
            :href="template.original_docx_template"
            download
            class="inline-flex items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900
              shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <DownloadIcon class="h-5 w-5 text-gray-500" />
            {{ t('download') }}
          </a>
        </div>
      </div>
    </div>

    <!-- Display template with placeholders if it's different from original -->
    <div
      v-if="showTemplateWithPlaceholders"
      class="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
    >
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-sm font-medium text-gray-700">{{ t('templateWithPlaceholders') }}</h3>
          <p class="truncate text-sm text-gray-900">{{ docxTemplateCleanUrl }}</p>
        </div>
        <div class="flex gap-2">
          <a
            :href="template.docx_template || ''"
            download
            class="inline-flex items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900
              shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <DownloadIcon class="h-5 w-5 text-gray-500" />
            {{ t('download') }}
          </a>
        </div>
      </div>
    </div>

    <!-- Hand in for Review Button -->
    <div
      v-if="
        !template.is_jupus_template &&
        !templateUnderReview &&
        !templateApproved &&
        template.docx_template
      "
      class="mb-6 mt-6 flex justify-end"
    >
      <button
        class="inline-flex items-center gap-x-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold
          text-white shadow-sm hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500
          disabled:pointer-events-none disabled:opacity-50"
        :disabled="isSubmittingForReview"
        @click="requestReview"
      >
        <svg
          v-if="isSubmittingForReview"
          class="h-5 w-5 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        {{ t('submitForReview') }}
      </button>
    </div>

    <!-- File upload field - only shown when no original document exists and not under review -->
    <FileField
      v-if="!template.original_docx_template && !template.is_jupus_template && !templateUnderReview"
      v-model="uploadedFile"
      name="document_template"
      label="Dokumentvorlage"
      :allowed-file-extensions="['docx']"
      @update:model-value="() => handleFormSave({ docx_template: uploadedFile })"
    />
  </SettingsPage>

  <!-- Confirmation Dialog -->
  <div
    v-if="showConfirmDialog"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
  >
    <div class="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
      <h3 class="mb-4 text-lg font-medium">{{ t('confirmTitle') }}</h3>
      <p class="mb-6 text-sm text-gray-600">
        {{ t('confirmMessage') }}
      </p>
      <div class="flex justify-end space-x-2">
        <button
          class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
            hover:bg-gray-50"
          @click="cancelReview"
        >
          {{ t('cancel') }}
        </button>
        <button
          class="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          :disabled="isSubmittingForReview"
          @click="confirmReview"
        >
          <span
            v-if="isSubmittingForReview"
            class="flex items-center"
          >
            <svg
              class="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ t('submitting') }}
          </span>
          <span v-else>{{ t('submitForReview') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
